import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { FaBrain, FaChartLine, FaEye, FaNetworkWired, FaExclamationTriangle, FaSyncAlt } from 'react-icons/fa';
import '../../Styles/ssai-monitoring.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SSAIMonitoring = () => {
  const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleString());
  const [refreshing, setRefreshing] = useState(false);
  
  // Simulated data state
  const [metrics, setMetrics] = useState({
    ai: { modelsTrained: 2, accuracy: 82, trainingPoints: 30, recommendations: 3 },
    analytics: { events: 171, fillRate: 68, revenue: 2456, responseTime: 245 },
    monitor: { components: 4, metrics: 15, alerts: 2, uptime: 99.9 },
    networks: [
      { id: 1, name: 'Test Network 1', priority: 8, fillRate: 85, status: 'online' },
      { id: 2, name: 'Test Network 2', priority: 6, fillRate: 78, status: 'online' }
    ],
    alerts: [
      { id: 1, type: 'warning', title: 'Fill Rate Below Threshold', message: 'Current: 68% | Threshold: 70% | Network: Test Network 1', time: '2 minutes ago' },
      { id: 2, type: 'critical', title: 'System Health Critical', message: 'Health check failed for external dependencies', time: '3 minutes ago' },
      { id: 3, type: 'info', title: 'Anomaly Detected', message: 'Response time spike detected: 2000ms (7.9σ from mean)', time: '5 minutes ago' }
    ]
  });

  const refreshInterval = useRef(null);

  const generateTimeLabels = (hours) => {
    const labels = [];
    for (let i = hours - 1; i >= 0; i--) {
      const time = new Date();
      time.setHours(time.getHours() - i);
      labels.push(time.getHours().toString().padStart(2, '0') + ':00');
    }
    return labels;
  };

  const generateRandomData = (points, min, max) => {
    return Array.from({ length: points }, () => Math.floor(Math.random() * (max - min + 1)) + min);
  };

  const [chartData, setChartData] = useState({
    ai: {
      labels: ['Fill Rate Predictor', 'Bid Optimizer', 'Engagement Predictor', 'Revenue Optimizer', 'Anomaly Detector'],
      datasets: [{
        data: [83, 77, 0, 0, 0],
        backgroundColor: ['#28a745', '#28a745', '#dc3545', '#dc3545', '#dc3545'],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    },
    analytics: {
      labels: generateTimeLabels(24),
      datasets: [{
        label: 'Events/Hour',
        data: generateRandomData(24, 20, 70),
        borderColor: '#4834d4',
        backgroundColor: 'rgba(72, 52, 212, 0.1)',
        fill: true,
        tension: 0.4
      }, {
        label: 'Fill Rate %',
        data: generateRandomData(24, 60, 90),
        borderColor: '#00d2d3',
        backgroundColor: 'rgba(0, 210, 211, 0.1)',
        fill: false,
        yAxisID: 'y1',
        tension: 0.4
      }]
    },
    monitor: {
      labels: ['Response Time', 'CPU Usage', 'Memory Usage', 'Network Latency', 'Error Rate'],
      datasets: [{
        label: 'Current Values',
        data: [245, 35, 68, 45, 8.2],
        backgroundColor: ['#00d2d3', '#54a0ff', '#5f27cd', '#00d2d3', '#ff6b6b'],
        borderRadius: 5
      }]
    },
    trends: {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [{
        label: 'Revenue ($)',
        data: [2156, 2298, 2445, 2387, 2456, 2523, 2612],
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        fill: false,
        tension: 0.4
      }, {
        label: 'Fill Rate (%)',
        data: [72, 68, 71, 69, 68, 70, 73],
        borderColor: '#ffc107',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
        fill: false,
        tension: 0.4
      }]
    }
  });

  const refreshData = () => {
    setRefreshing(true);
    setTimeout(() => {
      setMetrics(prev => ({
        ...prev,
        ai: { ...prev.ai, trainingPoints: prev.ai.trainingPoints + Math.floor(Math.random() * 5), recommendations: Math.floor(Math.random() * 3) + 2 },
        analytics: {
          ...prev.analytics,
          events: prev.analytics.events + Math.floor(Math.random() * 10),
          fillRate: Math.floor(Math.random() * 15 + 65),
          revenue: prev.analytics.revenue + Math.floor(Math.random() * 50),
          responseTime: Math.floor(Math.random() * 50 + 200)
        }
      }));
      
      setChartData(prev => ({
        ...prev,
        analytics: {
          ...prev.analytics,
          datasets: [
            { ...prev.analytics.datasets[0], data: generateRandomData(24, 20, 70) },
            { ...prev.analytics.datasets[1], data: generateRandomData(24, 60, 90) }
          ]
        },
        monitor: {
          ...prev.monitor,
          datasets: [{ ...prev.monitor.datasets[0], data: [Math.random() * 100 + 150, Math.random() * 40 + 20, Math.random() * 30 + 50, Math.random() * 50 + 20, Math.random() * 10 + 2] }]
        }
      }));
      
      setLastUpdated(new Date().toLocaleString());
      setRefreshing(false);
    }, 1000);
  };

  useEffect(() => {
    refreshInterval.current = setInterval(refreshData, 15000);
    return () => clearInterval(refreshInterval.current);
  }, []);

  return (
    <div className="ssai-monitoring-container p-4">
      <div className="header text-white mb-4">
        <h2><FaSyncAlt className={refreshing ? 'fa-spin' : ''} onClick={refreshData} style={{cursor: 'pointer'}} /> SSAI Real-time Monitoring</h2>
        <p className="opacity-75">AI-Powered Ad Server Performance Dashboard</p>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <div className="status-card h-100 text-center p-3 bg-white rounded shadow-sm">
            <FaBrain className="text-success fs-2 mb-2" />
            <h5 className="mb-1">AI Optimizer</h5>
            <span className="badge bg-success">Operational</span>
          </div>
        </div>
        <div className="col-md-3">
          <div className="status-card h-100 text-center p-3 bg-white rounded shadow-sm">
            <FaChartLine className="text-primary fs-2 mb-2" />
            <h5 className="mb-1">Analytics</h5>
            <span className="badge bg-primary">Processing</span>
          </div>
        </div>
        <div className="col-md-3">
          <div className="status-card h-100 text-center p-3 bg-white rounded shadow-sm">
            <FaEye className="text-info fs-2 mb-2" />
            <h5 className="mb-1">Monitoring</h5>
            <span className="badge bg-info">Active</span>
          </div>
        </div>
        <div className="col-md-3">
          <div className="status-card h-100 text-center p-3 bg-white rounded shadow-sm">
            <FaNetworkWired className="text-secondary fs-2 mb-2" />
            <h5 className="mb-1">Ad Networks</h5>
            <span className="badge bg-secondary">2 Online</span>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        {/* AI Performance */}
        <div className="col-md-6">
          <div className="card h-100 shadow-sm">
            <div className="card-header bg-white font-weight-bold d-flex align-items-center">
              <FaBrain className="me-2 text-danger" /> AI Performance Optimizer
            </div>
            <div className="card-body">
              <div className="row mb-3 text-center">
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">{metrics.ai.modelsTrained}</div>
                    <small className="text-muted">Models</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">{metrics.ai.accuracy}%</div>
                    <small className="text-muted">Accuracy</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">{metrics.ai.trainingPoints}</div>
                    <small className="text-muted">Points</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">{metrics.ai.recommendations}</div>
                    <small className="text-muted">Active</small>
                  </div>
                </div>
              </div>
              <div style={{ height: '250px' }}>
                <Doughnut data={chartData.ai} options={{ maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>

        {/* Real-time Analytics */}
        <div className="col-md-6">
          <div className="card h-100 shadow-sm">
            <div className="card-header bg-white font-weight-bold d-flex align-items-center">
              <FaChartLine className="me-2 text-primary" /> Advanced Analytics
            </div>
            <div className="card-body">
              <div className="row mb-3 text-center">
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">{metrics.analytics.events}</div>
                    <small className="text-muted">Events</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">{metrics.analytics.fillRate}%</div>
                    <small className="text-muted">Fill Rate</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">${metrics.analytics.revenue}</div>
                    <small className="text-muted">Revenue</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="metric-box bg-light p-2 rounded">
                    <div className="h4 mb-0">{metrics.analytics.responseTime}ms</div>
                    <small className="text-muted">Response</small>
                  </div>
                </div>
              </div>
              <div style={{ height: '250px' }}>
                <Line 
                  data={chartData.analytics} 
                  options={{ 
                    maintainAspectRatio: false,
                    scales: {
                      y: { position: 'left' },
                      y1: { position: 'right', min: 0, max: 100 }
                    }
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Ad Networks Status */}
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-header bg-white font-weight-bold">
              <FaNetworkWired className="me-2" /> Ad Networks
            </div>
            <ul className="list-group list-group-flush">
              {metrics.networks.map(network => (
                <li key={network.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold">{network.name}</div>
                    <small className="text-muted">RTB • Priority: {network.priority}</small>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold text-success">{network.fillRate}%</div>
                    <span className="badge rounded-pill bg-success">Online</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-header bg-white font-weight-bold">
              <FaExclamationTriangle className="me-2 text-warning" /> Recent Alerts
            </div>
            <div className="card-body p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {metrics.alerts.map(alert => (
                <div key={alert.id} className={`alert alert-${alert.type} m-2 p-2`} style={{ fontSize: '0.85rem' }}>
                  <div className="fw-bold">{alert.title}</div>
                  <div>{alert.message}</div>
                  <small className="opacity-75">{alert.time}</small>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Performance Trends */}
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-header bg-white font-weight-bold">
              Performance Trends (7 Days)
            </div>
            <div className="card-body">
              <div style={{ height: '200px' }}>
                <Line data={chartData.trends} options={{ maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer text-center mt-4 text-white opacity-50">
        <small>Last updated: {lastUpdated} • SSAI Microservice v1.0.0</small>
      </div>
    </div>
  );
};

export default SSAIMonitoring;
